import React, { useCallback, useEffect, useState } from 'react';

import { UserPlus } from 'lucide-react';

import { apiGetAsUser } from '../../lib/api_v2';
import { logError } from '../../lib/logger';
import { openInvitePeopleFromOnboarding } from '../../lib/onboardingCardActions';

import Flexicon from './Flexicon';
import { OnboardingCard, OnboardingCardGrid, OnboardingCardTile } from './OnboardingCard';

// Three suggestions plus the "invite someone else" tile fills the card's 4-up row.
const SUGGESTION_COUNT = 3;

interface SuggestedUser {
  blurb?: string | null;
  full_name?: string | null;
  id: string;
  shared_chat_count?: number | null;
}

// Same wording as InvitePeopleModal so a person reads the same subtitle in both places.
function mutualSubtitle(user: SuggestedUser): string {
  const count = Number(user.shared_chat_count) || 0;
  if (count === 1) return '1 mutual';
  if (count > 1) return `${count} mutuals`;
  return user.blurb || '';
}

interface InvitePeopleCardProps {
  className?: string;
  onDismiss: () => void;
  workspaceId?: string | null;
}

function InvitePeopleCard({ className, onDismiss, workspaceId }: InvitePeopleCardProps) {
  const [users, setUsers] = useState<SuggestedUser[]>([]);

  useEffect(
    function loadSuggestedUsers() {
      const abortController = new AbortController();

      async function fetchSuggestions() {
        try {
          const path = workspaceId ? `/suggested_users?${new URLSearchParams({ workspace_id: workspaceId })}` : '/suggested_users';
          const response = await apiGetAsUser(path, { signal: abortController.signal });
          const data = await response.json();

          if (abortController.signal.aborted) return;
          if (!response.ok || !data?.ok || !Array.isArray(data.data?.users)) return;

          setUsers(data.data.users.slice(0, SUGGESTION_COUNT));
        } catch (error) {
          if (abortController.signal.aborted) return;
          logError('InvitePeopleCard', 'Failed to load suggested users', { error });
        }
      }

      fetchSuggestions();

      return () => abortController.abort();
    },
    [workspaceId],
  );

  // A face opens the sheet with that person already selected; the +tile opens it empty.
  const handleUserClick = useCallback(
    (user: SuggestedUser) => {
      openInvitePeopleFromOnboarding(user);
      // Opening the real invite flow counts as acting on the card, so it retires.
      onDismiss();
    },
    [onDismiss],
  );

  const handleInviteClick = useCallback(() => {
    openInvitePeopleFromOnboarding();
    onDismiss();
  }, [onDismiss]);

  return (
    <OnboardingCard className={className} dismissLabel="Dismiss invite people" onDismiss={onDismiss} title="Invite People">
      <OnboardingCardGrid>
        {users.map((user) => (
          <button
            key={user.id}
            type="button"
            data-app-region="none"
            onClick={() => handleUserClick(user)}
            className="flex h-19 w-full min-w-0 cursor-pointer flex-col items-center gap-1 rounded-lg transition-[background-color,filter] hover:brightness-95"
          >
            <Flexicon resource={user} size="size-8" className="size-8 shrink-0 rounded-full" />
            {/* Name and mutuals read as one block, so the gap belongs between the avatar and
                the pair — not between the two lines of text. */}
            <span className="flex w-full min-w-0 flex-col items-center">
              <span className="w-full min-w-0 truncate text-center text-cursed leading-5 font-medium text-black dark:text-neutral-100">
                {user.full_name}
              </span>
              <span className="w-full min-w-0 truncate text-center text-xs/4 text-gray-400 dark:text-neutral-500">
                {mutualSubtitle(user)}
              </span>
            </span>
          </button>
        ))}
        <OnboardingCardTile accessibleLabel="Invite people" onClick={handleInviteClick}>
          <UserPlus size={18} strokeWidth={1.5} absoluteStrokeWidth />
        </OnboardingCardTile>
      </OnboardingCardGrid>
    </OnboardingCard>
  );
}

export default InvitePeopleCard;
